import React from 'react'
import { AppImg, AppSvg, CoinIcon, FormattedHTMLMessage, newIcons, useImageSrc } from '@web/walter-ui-kit'

import cn from 'classnames'
import { GetCard } from '@web/walter-lib/api'
import s from './s.module.css'
import { lang } from './lang'
import { images } from '~constants/images'

interface Props {
    designId?: string
    // eslint-disable-next-line react/no-unused-prop-types
    cardType?: string
    currency?: string
    className?: string
    showBlurredText?: boolean
    showBlurredContent?: boolean
    blurredImgSize?: number
    status?: GetCard['status']
}

export const getCardType = (cardType?: string) => {
    if (cardType === 'VIRTUAL') {
        return <FormattedHTMLMessage {...lang.virtual} className={s.tableText} />
    }

    if (cardType === 'PHYSICAL') {
        return <FormattedHTMLMessage {...lang.physical} className={s.tableText} />
    }

    return cardType
}

const getBlurredContent = (
    status: Props['status'],
    showBlurredText: Props['showBlurredText'],
    blurredImgSize: Props['blurredImgSize']
) => {
    if (status === 'FAILED') {
        return (
            <div className={s.blurredContent}>
                <AppSvg svgName={newIcons.ic24Unavailable} size={blurredImgSize} path="new-icons" />
                {showBlurredText && <FormattedHTMLMessage {...lang.failed} />}
            </div>
        )
    }

    if (status === 'BLOCKED') {
        return (
            <div className={s.blurredContent}>
                <AppSvg svgName={newIcons.ic24Lock} size={blurredImgSize} path="new-icons" />
                {showBlurredText && <FormattedHTMLMessage {...lang.blocked} />}
            </div>
        )
    }

    if (status === 'SUSPENDED') {
        return (
            <div className={s.blurredContent}>
                <AppSvg svgName={newIcons.ic24Snowflake} size={blurredImgSize} path="new-icons" />
                {showBlurredText && <FormattedHTMLMessage {...lang.frozen} />}
            </div>
        )
    }

    return null
}

// eslint-disable-next-line complexity
function CardImage({
    className,
    designId,
    currency,
    status,
    showBlurredText = true,
    showBlurredContent = true,
    blurredImgSize = 40,
}: Props) {
    const { src: defaultCard } = useImageSrc({
        imgName: images.card,
        path: 'common',
    })

    const isBlurred = status === 'FAILED' || status === 'BLOCKED' || status === 'SUSPENDED'

    return (
        <div className={cn(s.img, className)}>
            <AppImg
                imgName={`cards/${designId}.png` ?? images.card}
                onError={e => {
                    if (e.currentTarget.src !== defaultCard) {
                        e.currentTarget.src = defaultCard
                    }
                }}
                themed={false}
                path="common"
                className={cn(isBlurred && s.mainImgFilter)}
            />
            {isBlurred && showBlurredContent && getBlurredContent(status, showBlurredText, blurredImgSize)}
            {currency && (
                <div className={s.currency}>
                    <CoinIcon code={currency || ''} size="xs" />
                    {currency}
                </div>
            )}
        </div>
    )
}

export default CardImage
